/**
 * Phase 66 Plan 05a — Audit API client (port of `js/admin/audit-api.js`).
 *
 * GET /api/v1/admin/audit
 *
 * Query params (all optional):
 *   source          — minio | srvfs | kiosk | pg-export | provisioning_queue | machines
 *   admin_user_id   — admin user identifier
 *   date_from       — ISO date
 *   date_to         — ISO date
 *   status          — pending | ok | error | timeout | partial
 *   correlation_id  — UUID or prefix
 *   limit           — page size (always sent)
 *   cursor          — opaque cursor (omitted on first page)
 *
 * Backend response shape: { rows | items, next_cursor, total_estimated }
 * normalised here to: { items, next_cursor, total_visible }.
 */

import { apiGet } from "./client";

export interface AuditFilters {
  source?: string;
  admin_user_id?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
  correlation_id?: string;
}

export interface AuditEvent {
  ts: string;
  source: string;
  operation: string;
  admin_email: string;
  admin_user_id?: string | number;
  target_path?: string;
  correlation_id?: string;
  status: string;
  /** Optional fields used by deep-link builder. */
  bucket?: string;
  object_key?: string;
  cpu_id?: string;
  target_id?: string;
}

export interface AuditPage {
  items: AuditEvent[];
  next_cursor: string | null;
  total_visible: number;
}

interface RawAuditResponse {
  rows?: AuditEvent[];
  items?: AuditEvent[];
  next_cursor?: string | null;
  total_estimated?: number;
  total_visible?: number;
}

/**
 * List audit events with optional filters and cursor-based pagination.
 *
 * 401/429 handling lives in the shared apiRequest wrapper (authBus +
 * rateLimitStore). Other non-2xx throws ApiError with `.status` set.
 */
export async function listAudit(
  filters: AuditFilters,
  cursor: string | null,
  limit = 50,
): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (filters.source) params.set("source", filters.source);
  if (filters.admin_user_id) params.set("admin_user_id", filters.admin_user_id);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.status) params.set("status", filters.status);
  if (filters.correlation_id) params.set("correlation_id", filters.correlation_id);
  if (cursor) params.set("cursor", cursor);
  params.set("limit", String(limit));

  const data = await apiGet<RawAuditResponse>(`/admin/audit?${params.toString()}`);
  return {
    items: data.rows ?? data.items ?? [],
    next_cursor: data.next_cursor ?? null,
    total_visible:
      data.total_estimated != null ? data.total_estimated : data.total_visible ?? 0,
  };
}
